/**
 * Wire contract for GET /api/admin/stream/metrics.
 *
 * These interfaces mirror the backend payloads field-for-field. Nothing here is
 * derived or convenient — derived shapes live in the store, so a contract change
 * shows up as a compile error in exactly one place.
 */

/* ── Connection lifecycle ─────────────────────────────────────────────────── */

/**
 * `connecting`   first handshake, nothing on screen yet
 * `live`         an event arrived within the freshness window
 * `stale`        socket is open but silent past STALE_AFTER_MS — the backend
 *                ticks every 2s, so silence is a fault, not idleness
 * `disconnected` transport dropped; the store is backing off and retrying
 * `forbidden`    authenticated, but without `platform_staff`. Terminal for the
 *                stream: reconnecting cannot fix a missing role, so the store
 *                stops trying and the screen says so instead of blinking
 *                "reconnecting" forever
 */
export type ConnectionState = 'connecting' | 'live' | 'stale' | 'disconnected' | 'forbidden'

export const SSE_EVENT_NAMES = ['snapshot', 'metrics', 'fleet', 'alert'] as const
export type SseEventName = (typeof SSE_EVENT_NAMES)[number]

/* ── metrics (every 2s) ───────────────────────────────────────────────────── */

export interface IngestMetrics {
  events_per_sec_received: number
  events_per_sec_accepted: number
  events_per_sec_rejected: number
  channel_depth: number
  channel_capacity: number
  /** Server-computed. Treated as authoritative; depth/capacity is the fallback. */
  channel_depth_pct: number
  active_connections: number
}

/**
 * Cumulative ingestion counters, summed across every instance.
 *
 * Absolute values, never deltas: each tick carries the complete figure, which is
 * the exact opposite of `throughput_series` in the same payload. Nothing here
 * may be differenced to obtain a rate — `ingest.events_per_sec_received` is the
 * rate, already a ~10s sliding window and already correct across a reconnect or
 * a restart, and the difference of two totals is wrong in both of those cases.
 *
 * 64-bit integers on the wire. They fit in a JS `number` with room to spare —
 * the safe ceiling of 2^53 is millennia away at 40k ev/s — but a typed layer
 * anywhere in between must use long/bigint, never int32.
 */
export interface IngestTotals {
  /** Since the beginning, across the whole fleet of instances. */
  received: number
  accepted: number
  rejected: number
  /**
   * Current UTC day. These roll over at midnight **UTC**, not in the operator's
   * zone — in Brazil "today" resets at 21:00 — so anything rendering them has to
   * say UTC or it gets reported as a bug.
   */
  today_received: number
  today_accepted: number
  today_rejected: number
  /**
   * The figure is partial: this instance has not had the others' contribution
   * confirmed yet. It stays correct for this instance, so it is *marked and
   * never hidden* — the same treatment `reference_stale` gets on the fleet.
   *
   * The common case is not an incident: it is the first seconds after a deploy,
   * before the first confirmation, where the number shown is well below the real
   * one and will jump when confirmation lands.
   */
  stale: boolean
  /**
   * Age of the last confirmation. `-1` is the sentinel for "no confirmation
   * yet" — not an error and not a negative second — and always arrives with
   * `stale: true`.
   */
  age_seconds: number
}

/**
 * The eleven reasons, in the order the backend sends them.
 *
 * The contract guarantees all eleven on every tick, zeros included, and this
 * array is what the panel renders from — so a row never appears or disappears
 * because the attack of the moment changed. A reason outside this list still
 * renders, at the end, with its raw string: a new backend reason should show up
 * as an unlabelled row, not vanish.
 */
export const REJECTION_REASONS = [
  'auth_failed',
  'rate_limited',
  'invalid_endpoint_id',
  'endpoint_id_mismatch',
  'invalid_event_type',
  'empty_payload',
  'payload_too_large',
  'payload_not_json',
  'missing_event_time',
  'invalid_event_time',
  'unsupported_schema_version',
] as const

export type RejectionReason = (typeof REJECTION_REASONS)[number]

export interface RejectionBucket {
  /** Typed as string on purpose: an unknown reason must render, not be dropped. */
  reason: string
  count: number
}

export interface LatencyMs {
  p50: number
  p95: number
  p99: number
}

export interface BatchingMetrics {
  avg_batch_size: number
  avg_flush_ms: number
}

export interface RuntimeMetrics {
  managed_memory_mb: number
  gc_pause_ms_last_minute: number
  gen2_collections_last_minute: number
}

export interface ThroughputPoint {
  /** ISO-8601 UTC instant. */
  t: string
  events_per_sec: number
}

export interface MetricsPayload {
  server_time: string
  ingest: IngestMetrics
  /**
   * Null **only** when the payload omitted the block — a server older than the
   * field, or a rollout in progress. The decoder neither invents zeros for it (a
   * fabricated 0 beside a real 4.18 billion is a lie the screen renders
   * beautifully) nor rejects the whole tick over it, which would take latency,
   * rejections, runtime and the chart down with one missing block.
   */
  totals: IngestTotals | null
  rejections_last_hour: RejectionBucket[]
  latency_ms: LatencyMs
  batching: BatchingMetrics
  runtime: RuntimeMetrics
  /** 30 minutes on `snapshot`; the newest point only on `metrics` ticks. */
  throughput_series: ThroughputPoint[]
}

/* ── fleet (every 10s) ────────────────────────────────────────────────────── */

export interface AgentCounts {
  total_provisioned: number
  connected: number
  /** The only count that means "act now": reporting stopped inside active hours. */
  anomalous_silence: number
  expected_offline: number
  never_connected: number
}

/**
 * `never_connected`    provisioned, never reported — an install problem
 * `anomalous_silence`  stopped reporting INSIDE its historical active window.
 *                      In an EDR this may mean the agent was killed on the box
 * `expected_offline`   silent outside the historical window, or no baseline yet
 */
export type SilenceState = 'never_connected' | 'anomalous_silence' | 'expected_offline'

export interface SilentAgent {
  endpoint_id: string
  hostname: string
  tenant_name: string
  /** Null only when `state` is `never_connected` — the host never reported. */
  last_seen: string | null
  silent_for_minutes: number
  state: SilenceState
  /**
   * Historical active window in UTC, "HH:00-HH:00". Null while the endpoint has
   * under 7 days of history — and an endpoint without a baseline is never
   * `anomalous_silence`, because silence on a freshly installed machine is a
   * guaranteed false positive.
   *
   * The window may wrap midnight ("22:00-04:00" is a night-shift machine), so
   * nothing may assume `from < to`.
   */
  typical_active_hours: string | null
}

export interface TenantVolume {
  tenant_id: string
  name: string
  events_per_sec: number
  endpoints: number
  events_per_endpoint: number
  /** Null when the tenant has no contracted quota. Do not invent a ceiling. */
  quota_events_per_sec: number | null
  /** Null exactly when `quota_events_per_sec` is null. */
  quota_usage_pct: number | null
  /**
   * Server's verdict, computed on events-per-endpoint at 3 robust deviations
   * from the fleet median — not on absolute volume. The client also computes
   * one; both are shown, and the client never overrides the server.
   */
  is_outlier: boolean
}

export interface AgentVersion {
  version: string
  count: number
  has_known_issue: boolean
}

export interface FleetPayload {
  agents: AgentCounts
  silent_agents: SilentAgent[]
  tenants: TenantVolume[]
  agent_versions: AgentVersion[]
  enrollments_last_24h: number
  reconnect_rate_per_min: number
  /**
   * Reference data (tenant names, quotas, versions, fleet) comes from Postgres
   * with a 10s cache; the metrics beside it come from in-memory counters and
   * stay exact regardless. When the database is unreachable the server keeps
   * serving the last good value and raises this flag — the fleet section must
   * be marked as lagging, and the metrics must NOT be hidden along with it.
   */
  reference_stale: boolean
  /** Age of the cached reference data. `-1` = no successful cycle since boot. */
  reference_age_seconds: number
}

/* ── snapshot (on every (re)connect) ──────────────────────────────────────── */

/**
 * Sent once per connection, before anything else — and omitted entirely when the
 * server has just booted and the aggregator has not finished its first cycle. In
 * that case the first thing to arrive is a `metrics` tick, within 2s.
 */
export interface SnapshotPayload {
  metrics: MetricsPayload
  fleet: FleetPayload
  /** Open alerts, oldest first, up to 100 — so the rail is not blank on open. */
  alerts: AlertPayload[]
}

/* ── alert (discrete) ─────────────────────────────────────────────────────── */

export type AlertSeverity = 'info' | 'warning' | 'critical'

export const ALERT_KINDS = [
  'agent_silence',
  'queue_saturation',
  'auth_spike',
  'tenant_quota',
  'agent_version_issue',
] as const

export type AlertKind = (typeof ALERT_KINDS)[number]

export interface AlertPayload {
  /**
   * Lexicographically ordered by occurrence, so it sorts and dedupes without
   * reparsing the date. Alerts fire on the TRANSITION, not repeatedly while the
   * condition lasts — a re-sent id is the same event, never a new one.
   */
  id: string
  severity: AlertSeverity
  /** Typed loosely: a kind the backend adds later must render, not disappear. */
  kind: string
  message: string
  /** Absent for platform-wide alerts that belong to no single tenant. */
  tenant_name?: string | null
  occurred_at: string
}

/* ── logs: GET /api/admin/stream/logs e GET /api/admin/logs ────────────────── */

/**
 * A linha de log. Uma só forma nos dois caminhos — stream e histórico.
 *
 * ⚠️ Exatamente UM de `id` / `event_id` vem preenchido, e é ele que decide como
 * buscar o detalhe (§3.1). O `COPY` binário do PostgreSQL grava sem devolver os
 * `id` gerados, então a linha que aparece ao vivo não conhece o próprio
 * identificador durável.
 */
export interface LogRow {
  /** Identificador durável. Preenchido SÓ em linha vinda do banco. */
  id: number | null
  /** Identificador sintético, ordenável por tempo. Preenchido SÓ em linha do tail. */
  event_id: string | null
  /** Relógio do ENDPOINT. É o que o analista pensa como "quando aconteceu". */
  event_time: string
  /** Relógio do SERVIDOR. É o eixo confiável de qualquer investigação. */
  received_at: string
  tenant_id: string
  /** Resolvido no servidor. Null quando o diretório ainda não conhece a máquina. */
  tenant_name: string | null
  endpoint_id: string
  /** Resolvido no servidor. Null pelo mesmo motivo. */
  hostname: string | null
  /** snake_case, normalizado na ingestão. Ex.: "process_create". */
  event_type: string
  /** Canal de origem no agente. No Windows: "Security", "Windows PowerShell"… */
  source: string | null
  /** Identificador na plataforma de origem. No Event Log: "4688". */
  native_id: string | null
  /** Número do registro na origem. Buraco na sequência = coleta perdeu eventos. */
  record_id: number | null
  /** Tamanho aproximado do payload. Serve para decidir se vale expandir. */
  payload_size: number | null
}

/**
 * O resumo estruturado de um evento, projetado das COLUNAS tipadas do banco
 * (§HERMES_MIGRATION.md). É a fonte da ficha de investigação e dos pivôs — o
 * cliente não precisa mais cavar o `payload` para montá-la.
 *
 * ⚠️ Todo campo é anulável: ausência no evento vira `null`, e a interface só
 * desenha o que veio (§20). O objeto `facets` em si **sempre existe** — quem pode
 * faltar é o campo dentro dele. Os enums chegam como TEXTO estável
 * (`"success"`, `"outbound"`, `"high"`), nunca número: são lidos como string e
 * um valor não reconhecido ainda renderiza, em vez de sumir (§2).
 *
 * GUIDs (`process_entity_id`) vêm canônicos e minúsculos, sem chaves; IPs como
 * texto; portas como número. Os `related_*` são os pivôs de fan-out da frota.
 */
export interface LogFacets {
  /** Verbo fino do evento (ECS `event.action`). Ex.: `network_connect`. */
  event_action: string | null
  /** Categorias ECS. Ex.: `["network"]`. */
  event_category: string[] | null
  /** `success` | `failure` | `unknown` | null. */
  event_outcome: string | null
  /**
   * `sysmon`|`security`|`powershell`|`rdp`|`task_scheduler`|`wmi`|`bits`|`system`
   * |`etw`|`wfp`|`ebpf`|`journald`|`proc`|`unknown`|null. Os cinco últimos módulos
   * cobrem a telemetria Linux ( eBPF, journald, /proc) e os canais nativos de
   * Windows (ETW, WFP) — texto de fio, lido como string e renderizado mesmo se novo.
   */
  event_module: string | null
  /** Provider da plataforma de origem. Ex.: `Microsoft-Windows-Sysmon`. */
  event_provider: string | null
  /** ProcessGuid canônico (sem chaves). Pivô: timeline/árvore do processo. */
  process_entity_id: string | null
  /** ProcessGuid do pai. Pivô para subir a árvore. */
  process_parent_entity_id: string | null
  process_pid: number | null
  process_parent_pid: number | null
  process_executable: string | null
  /**
   * Nome curto do processo, sem o caminho. No Linux é o `comm` do kernel; no
   * Windows, o nome da imagem. Complementa `process_executable`, não o substitui.
   */
  process_name: string | null
  process_command_line: string | null
  /** `low`|`medium`|`high`|`system`|`unknown`|null. */
  process_integrity: string | null
  user_name: string | null
  user_domain: string | null
  user_sid: string | null
  host_hostname: string | null
  /** `windows`|`linux`|`mac_os`|`unknown`|null. */
  host_os: string | null
  source_ip: string | null
  source_port: number | null
  destination_ip: string | null
  destination_port: number | null
  /** `tcp`|`udp`|`icmp`|`unknown`|null. */
  network_transport: string | null
  /** `inbound`|`outbound`|`unknown`|null. */
  network_direction: string | null
  dns_question: string | null
  file_path: string | null
  /** Nome do arquivo, sem o diretório. Ex.: `mimikatz.exe`. */
  file_name: string | null
  /** Extensão normalizada, sem ponto. Ex.: `exe`, `dll`, `sh`. */
  file_extension: string | null
  registry_path: string | null
  /** Nome do serviço/unidade. No Windows, o serviço; no Linux, a unit systemd. */
  service_name: string | null
  /** `running`|`stopped`|`start_pending`|… — texto de fio, lido como string. */
  service_state: string | null
  /** Pivô de fan-out: quem falou com este IP. */
  related_ip: string[] | null
  /** Pivô de fan-out: o que este usuário fez na frota. */
  related_user: string[] | null
  /** Pivô de caça a IOC: todo host que viu este binário. */
  related_hash: string[] | null
  /**
   * O criador REAL do processo. Presente só quando difere do pai declarado —
   * que é exatamente o PPID spoofing (§12.1). Ausente no caso normal.
   */
  process_creator_entity_id: string | null
  process_creator_pid: number | null
}

/**
 * Inspeção AMSI de um script (`command_execution` + `amsi_scan`, §12.1).
 *
 * `content` é o script DESOFUSCADO, até 32 KiB — texto hostil por definição. A
 * interface o mostra como código e nunca o interpreta.
 */
export interface AmsiScan {
  app_name: string | null
  content_name: string | null
  content: string | null
  content_truncated: boolean
  content_size: number | null
  original_size: number | null
  hash: string | null
  scan_result: number | null
  /** Null quando o campo não veio: "não sabemos" não é "limpo". */
  detected: boolean | null
}

export interface DnsDetails {
  question_name: string | null
  /** `A`, `AAAA`, `CNAME`… — texto de fio. */
  question_type: string | null
  /** Código de resposta numérico (DNS_ERROR_* no Windows). */
  response_code: number | null
  answers: string[] | null
}

/**
 * Campos lidos do PAYLOAD, não de coluna (§12.1). O objeto sempre existe; cada
 * campo é anulável, como em `facets`.
 */
export interface LogDetails {
  /** Linux: argv como lista. */
  process_args: string[] | null
  /** Nulo também quando o processo morreu por sinal (Linux). */
  process_exit_code: number | null
  registry_data: string | null
  /** REG_* numérico. */
  registry_data_type: number | null
  registry_data_size: number | null
  amsi: AmsiScan | null
  dns: DnsDetails | null
  /** Alerta do motor de detecção, quando o evento é um (`event_type = alert`). */
  alert: AlertDetails | null
}

export interface LogDetail {
  row: LogRow
  /** Envelope que trouxe o evento. Rastreia lineage agente → servidor. */
  batch_id: string | null
  /** Versão do agente que emitiu. Isola bug introduzido por release. */
  agent_version: string | null
  /** O evento completo, como veio. Objeto JSON arbitrário. Fonte forense (§19). */
  payload: unknown
  /**
   * O resumo estruturado, tipado, das colunas. SEMPRE presente; cada campo é
   * anulável. É daqui que sai a ficha de investigação e os pivôs (§6, §9).
   */
  facets: LogFacets
  /** Campos do payload (AMSI, DNS, argv, registro). SEMPRE presente (§12.1). */
  details: LogDetails
}

/* ── processos: GET /api/admin/processes/{entityId}[/timeline] (§12) ───────── */

/**
 * De onde veio o nó:
 *  `process_create` — o nascimento foi visto; o nó é completo.
 *  `first_event`    — já existia quando o agente subiu; sem `process_create`.
 *  `unseen`         — só o id, citado por um filho; todo o resto é nulo.
 */
export type ProcessNodeSource = 'process_create' | 'first_event' | 'unseen'

export interface ProcessNode {
  /** GUID com hífens. As rotas aceitam também os 32 hex sem hífen. */
  entity_id: string
  source: ProcessNodeSource
  /** Linha do `process_create` → detalhe em `/logs/{event_id}`. */
  event_id: number | null
  tenant_id: string | null
  tenant_name: string | null
  endpoint_id: string | null
  hostname: string | null
  started_at: string | null
  ended_at: string | null
  exit_code: number | null
  parent_entity_id: string | null
  creator_entity_id: string | null
  creator_pid: number | null
  /** O criador real difere do pai declarado: PPID spoofing. */
  parent_spoofed: boolean
  pid: number | null
  parent_pid: number | null
  name: string | null
  executable: string | null
  command_line: string | null
  user_name: string | null
  user_domain: string | null
  integrity: string | null
  host_os: string | null
  /** Só vem preenchido nos filhos; null = não se sabe. */
  has_children: boolean | null
}

export interface ProcessTree {
  node: ProcessNode
  /** Do pai para cima, até a raiz conhecida. */
  ancestors: ProcessNode[]
  ancestors_truncated: boolean
  /** Filhos diretos, na ordem em que nasceram, até 200. */
  children: ProcessNode[]
  children_truncated: boolean
  /** O criador real — só com PPID spoofing. */
  creator: ProcessNode | null
}

/** `child_spawn` = o `process_create` de um filho; o summary é do FILHO. */
export type ProcessTimelineRelation = 'self' | 'child_spawn'

export interface ProcessTimelineSummary {
  event_action: string | null
  event_outcome: string | null
  process_entity_id: string | null
  process_name: string | null
  process_executable: string | null
  process_pid: number | null
  process_command_line: string | null
  file_path: string | null
  dns_question: string | null
  destination_ip: string | null
  destination_port: number | null
  registry_path: string | null
  related_hash: string[] | null
}

export interface ProcessTimelineItem {
  row: LogRow
  relation: ProcessTimelineRelation
  summary: ProcessTimelineSummary
}

export interface ProcessTimelinePage {
  items: ProcessTimelineItem[]
  page_size: number
  has_more: boolean
  next_cursor: string | null
  /** `event_time_asc` — crescente, ao contrário do histórico. */
  order: string
}

/**
 * Só dois nomes: `:open` e `:keepalive` são COMENTÁRIOS SSE e o `EventSource`
 * não os entrega a handler nenhum. Não são inúteis — o keepalive prova
 * vivacidade num filtro apontado para um agente quieto (§4.4).
 */
export const LOG_SSE_EVENT_NAMES = ['backlog', 'logs'] as const
export type LogSseEventName = (typeof LOG_SSE_EVENT_NAMES)[number]

/** O quadro `logs`, a cada 500 ms. Ver §4.2–§4.5. */
export interface LogFrame {
  server_time: string
  rows: LogRow[]
  /** Quantos eventos o filtro CASOU na janela. É a vazão real do recorte. */
  matched: number
  /** Quantos vieram em `rows`. */
  delivered: number
  /** Casaram e NÃO vieram, por causa do teto do quadro. */
  sampled_out: number
  /** O SERVIDOR não conseguiu mostrar. Deve ser 0; diferente disso é alarme. */
  lapped: number
  /** 'full' | 'partial' — cobertura do tail nesta instância. Ver §4.5. */
  coverage: string
  instances: number
  /** Duração da janela que produziu o quadro. */
  window_ms: number
}

/**
 * O `backlog`, uma vez por conexão: as últimas ~100 linhas do filtro, do banco.
 *
 * `degraded: true` significa "não consegui ler o histórico", e NÃO "não há
 * eventos" — o tail segue funcionando porque é memória (§4.7).
 */
export interface LogBacklog {
  server_time: string
  rows: LogRow[]
  degraded: boolean
}

/** Recorte do stream. A URL montada a partir dele é a identidade da conexão. */
export interface LogFilter {
  tenantId?: string
  endpointId?: string
  eventType?: string
  source?: string
}

/* ── detecções: GET /api/admin/detections ────────────────────────────────── */

/**
 * Severidade nominal da regra no Argos. Conjunto fechado; um valor desconhecido
 * cai em `null`, e a tela o mostra como "sem severidade" — inventar `low` faria
 * um alerta sumir da triagem, inventar `critical` faria um alarme falso.
 */
export type DetectionSeverity = 'low' | 'medium' | 'high' | 'critical'

export interface MitreRef {
  /** `TA0002`, `T1059.001`. */
  id: string
  name: string | null
}

/** Um alerta na fila de triagem. */
export interface DetectionAlert {
  /** `row.id` abre o detalhe completo em `/logs/{id}` (`details.alert`). */
  row: LogRow
  rule_id: string | null
  rule_name: string | null
  severity: DetectionSeverity | null
  /** 0 a 100. */
  risk_score: number | null
  /** `evento`, `sequencia`, `limiar`, `correlacao`. */
  mechanism: string | null
  tactics: MitreRef[]
  techniques: MitreRef[]
  /** Processo que fechou a detecção: abre a linha do tempo dele. */
  process_entity_id: string | null
  process_name: string | null
  process_pid: number | null
  process_command_line: string | null
  user_name: string | null
  host_hostname: string | null
  /** O que o agente fez por causa do alerta. Nunca null: vazia quando ele não agiu. */
  response: AlertResponseAction[]
}

/** Processo alvo de um `kill_process`. Todo campo é opcional. */
export interface AlertResponseTarget {
  pid: number | null
  name: string | null
  entity_id: string | null
  executable: string | null
  /** IP de quem varreu — alvo de um `block_ip`. */
  ip?: string | null
}

/**
 * Uma ação de resposta do agente (`data.response[]`).
 *
 * `action` e `status` são vocabulário ABERTO: `kill_process`/`isolate_host` e
 * `executed`/`simulated`/`skipped`/`failed` hoje, qualquer coisa amanhã. Valor
 * novo vai como veio e é exibido como veio; tipo errado vira `null`.
 */
export interface AlertResponseAction {
  action: string | null
  status: string | null
  /** Relógio do agente. */
  timestamp: string | null
  target: AlertResponseTarget | null
  detail: string | null
}

export interface DetectionPage {
  items: DetectionAlert[]
  page_size: number
  has_more: boolean
  next_cursor: string | null
  /** Sempre `id_desc` — ordem de chegada. */
  order: string
}

export interface AlertThreat {
  framework: string | null
  tactic: MitreRef | null
  technique: MitreRef | null
}

/** Uma condição da regra que o evento satisfez — o "por que disparou". */
export interface AlertCondition {
  stage: number | null
  field: string | null
  operator: string | null
  expected: string | null
  observed: string | null
  negated: boolean
}

/** Um evento que participou da detecção. */
export interface AlertEvidence {
  stage: number | null
  timestamp: string | null
  action: string | null
  code: string | null
  entity_id: string | null
  pid: number | null
  process_name: string | null
  command_line: string | null
  parent_name: string | null
  user: string | null
  file_path: string | null
  registry_path: string | null
  destination: string | null
  dns_question: string | null
}

/** O alerta inteiro, no detalhe do evento. */
export interface AlertDetails {
  rule_id: string | null
  rule_name: string | null
  rule_description: string | null
  rule_version: number | null
  ruleset: string | null
  references: string[]
  severity: DetectionSeverity | null
  risk_score: number | null
  mechanism: string | null
  key: string | null
  threats: AlertThreat[]
  explanation: AlertCondition[]
  evidence: AlertEvidence[]
  response: AlertResponseAction[]
}
